// Lists engine: a sponsor's ask in, a ranked list for Peter to review, a sponsor file out.
//
// Pure (no DOM, no fetch): the Lists page (client/lists/), the MCP tools (worker/mcp.ts) and the tests all call
// it. Two audiences never mix. Peter sees every reason, including the private ones. Sponsor rows carry only the
// people he kept and only reasons that are safe to show: never notes, holds, liquidity, scores or contact keys.

import { CAPITAL_TYPES, askedSectorsFromTouches, categorize, checkBandOf, parseMoneyRange, type CheckBand } from "./categorize";
import { TAPPED_OUT_DAYS } from "./engine";
import { INVESTOR_TYPES, REGIONS, SECTORS, SENSITIVE_WINDOW_DAYS } from "./network";

export type ClientView = "blind" | "named" | "full";
export const VIEW_LABEL: Record<ClientView, string> = {
  blind: "Profiles only, no names",
  named: "Names, firms and titles",
  full: "Names with email and phone",
};

/** Regions an ask can name ("Other" is where people with no known state land; not something a sponsor asks for). */
export const ASK_REGIONS = REGIONS.filter((r) => r !== "Other");

export interface Ask {
  id: string;
  /** Who asked: "Valkyrie (Austin, TX)". */
  client: string;
  /** What they want, in their words or Peter's. */
  text: string;
  received?: string;
  /** The first one is the main focus and counts most. */
  sectors: string[];
  /** Empty means any capital source. */
  types: string[];
  excludeTypes: string[];
  capitalOnly: boolean;
  minCheck: number | null;
  maxCheck: number | null;
  /** Only people in these regions (ignored when states are given). */
  regions: string[];
  /** Only people in these states. */
  states: string[];
  /** A bonus, not a filter. */
  preferRegions: string[];
  size: number;
  view: ClientView;
  createdAt?: string;
}

/** The fields of a network contact this engine reads. network.ts's Contact and slim.ts's SlimContact both fit. */
export interface ListContact {
  k?: string;
  id?: string;
  n: string;
  co?: string;
  lco?: string;
  ti?: string;
  ty?: string;
  ci?: string;
  st?: string;
  rg?: string;
  se?: string[];
  cmin?: number | null;
  cmax?: number | null;
  cs?: string;
  rs?: number;
  em?: string;
  ph?: string;
  no?: string;
  src?: string[];
  fl?: { flag: string; date?: string | null; source?: string; text?: string }[];
  pa?: { deal: string; outcome?: string; amount?: number; date?: string }[];
  bu?: string[];
  ji?: { sector: string; date?: string | null } | null;
  rt?: readonly (readonly (string | null)[])[];
  /** Sectors this person asked Peter for deals in, from the whole touch log. */
  as?: { sector: string; date?: string | null }[];
  /** Standing dislikes: "no real estate", "no venture". */
  np?: string[];
}

export interface PastDeal {
  deal_id: string;
  name?: string;
  sectors?: string | string[];
}

/** Peter's private overlay on one person (network.ts PrivateNote), as far as lists care. */
export interface Overlay {
  hold?: boolean;
  noPitch?: boolean;
  note?: string;
  sectors?: string[];
}

export interface EngineEnv {
  pastDeals?: PastDeal[];
  overlay?: (key: string) => Overlay | null | undefined;
  /** YYYY-MM-DD, the "as of" date for recency and hold windows. Defaults to today. */
  today?: string;
}

export interface Scored {
  c: ListContact;
  key: string;
  score: number;
  /** Why this person fits, for Peter's review. */
  why: string[];
  /** The subset that is safe to show a sponsor, phrased for the sponsor. */
  clientWhy: string[];
  /** Criteria this person misses for this ask. */
  misses: string[];
  /** Private reasons to leave this person off this list. Never exported. */
  heldFor: string[];
  /** Private context for Peter only (fresh liquidity, his own note). Never exported. */
  peterOnly: string[];
  band: CheckBand;
  /** Sectors of this ask the person covers. */
  focus: string[];
  type: string;
  /** The same facts as `why`, structured for compact display. */
  signals: { asked: string[]; askedWhen: string; invested: string[]; investedCount: number; typeMatch: boolean; checkFit: boolean; close: boolean };
}

export interface ListResult {
  ask: Ask;
  recommended: Scored[];
  held: Scored[];
  near: Scored[];
  /** People inside the ask's geography and type limits. */
  considered: number;
}

export type Decision = "Y" | "N";
export interface ReviewState {
  askId: string;
  /** Y = keep (also: include someone held back or a close miss). N = leave off. */
  decisions: Record<string, Decision>;
  /** People Peter added by hand. */
  added: string[];
  view?: ClientView;
  updatedAt?: string;
  /** Each download: when, which view, and who was in the file (so other asks can show "already sent"). */
  exports?: { at: string; view: ClientView; count: number; keys?: string[] }[];
}

/* ------------------------------------------------------------------ small helpers */

const SECTOR_SET = new Set(SECTORS);
const TYPE_SET = new Set(INVESTOR_TYPES);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY = 86400000;

export const keyOf = (c: ListContact): string => c.k || c.id || c.n;
const asSector = (s: unknown): string | null => (typeof s === "string" && SECTOR_SET.has(s) ? s : null);
const asType = (t: unknown): string => (typeof t === "string" && TYPE_SET.has(t) ? t : "Unknown");
const todayIso = () => new Date().toISOString().slice(0, 10);

export function monthLabel(d?: string | null): string {
  if (!d || !/^\d{4}-\d{2}/.test(d)) return "";
  return `${MONTHS[+d.slice(5, 7) - 1]} ${d.slice(0, 4)}`;
}
const daysBetween = (a: string, b: string) => (Date.parse(b) - Date.parse(a)) / DAY;

export function money(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n <= 0) return "";
  if (n >= 1e9) return `$${+(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${+(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${n}`;
}

/** Check size this person has actually stated (CRM columns or notes). Estimates from type don't count. */
export function statedCheck(c: ListContact): { min: number | null; max: number | null } | null {
  const src = (c.cs || "").toLowerCase();
  if (src.startsWith("estimated") || src === "unknown" || src === "not an investor") return null;
  const min = c.cmin || null;
  const max = c.cmax || null;
  return min == null && max == null ? null : { min, max };
}

export function bandOf(c: ListContact): CheckBand {
  if ((c.cs || "").toLowerCase() === "not an investor") return "Not an investor";
  const chk = statedCheck(c);
  return checkBandOf(chk?.min ?? null, chk?.max ?? null, asType(c.ty));
}

function askedSectors(c: ListContact): Map<string, string> {
  const out = new Map<string, string>();
  if (c.as) {
    for (const a of c.as) {
      const s = asSector(a.sector);
      if (s) out.set(s, a.date || "");
    }
    return out;
  }
  // networks built before `as` existed: the last few touches only
  const inbound = (c.rt ?? []).filter((t) => t && t[2] === "I").map((t) => ({ date: String(t[0] || ""), summary: String(t[3] || "") }));
  for (const a of askedSectorsFromTouches(inbound)) out.set(a.sector, a.date);
  return out;
}

const NO_INVESTMENTS = { sectors: new Set<string>(), count: 0 };

function investedSectors(c: ListContact, deals: Map<string, string[]>): { sectors: ReadonlySet<string>; count: number } {
  if (!c.pa?.length) return NO_INVESTMENTS;
  const sectors = new Set<string>();
  let count = 0;
  for (const p of c.pa ?? []) {
    if ((p.outcome || "").toLowerCase() !== "invested") continue;
    count++;
    for (const s of deals.get(p.deal) ?? []) sectors.add(s);
  }
  return { sectors, count };
}

const HOLD_LABEL: Record<string, string> = {
  "deceased": "Deceased",
  "loss of child": "Lost a child",
  "serious illness": "Serious illness",
  "divorce": "Going through a divorce",
  "inactive": "Marked inactive",
  "opted out": "Asked not to be contacted",
  "retired": "Done with private deals",
  "tapped out": "Tapped out",
};
/** Not in SENSITIVE_WINDOW_DAYS: "retired" holds until the note changes; "tapped out" as long as the deal matcher
 *  keeps them from a pitch (shared/engine.ts). */
const OTHER_WINDOWS: Record<string, number> = { "retired": Infinity, "tapped out": TAPPED_OUT_DAYS };

/** Private reasons in the network itself, with the windows the rest of the app uses. Flags dated after
 *  `today` haven't happened yet as of that day. */
export function autoHolds(c: ListContact, today: string): string[] {
  const out: string[] = [];
  for (const f of c.fl ?? []) {
    if (f.date && f.date >= today) continue;
    const win = SENSITIVE_WINDOW_DAYS[f.flag] ?? OTHER_WINDOWS[f.flag];
    if (win == null || (f.date && daysBetween(f.date, today) > win)) continue;
    const label = HOLD_LABEL[f.flag] ?? f.flag;
    out.push(f.date ? `${label} (${monthLabel(f.date)})` : label);
  }
  return out;
}

/** Things they said no to, and the words in an ask that trip them. */
const NO_TO: Record<string, [RegExp, string]> = {
  "no real estate": [/real estate|\bproperty\b|multifamily|\bREIT\b/i, "Won't do real estate"],
  "no venture": [/venture|\bseed\b|start-?up|early[- ]stage|series [ab]\b/i, "Won't do venture"],
  "no funds": [/\bfund\b|blind[- ]pool/i, "Won't invest in funds"],
  "no crypto": [/crypto|bitcoin|blockchain|token/i, "Won't do crypto"],
  "no cannabis": [/cannabis|marijuana|\bhemp\b/i, "Won't do cannabis"],
};
const NO_TO_NOTES: [string, RegExp][] = [["no real estate", /hates real estate|no real estate/i], ["no venture", /no venture/i]];

/** What this person has said no to: the network's own list, or (networks built before it existed) the notes. */
export function saidNoTo(c: { np?: string[]; no?: string }): string[] {
  return c.np ?? NO_TO_NOTES.filter(([, r]) => r.test(c.no || "")).map(([k]) => k);
}

/* ------------------------------------------------------------------ asks */

export function normalizeAsk(a: Partial<Ask> & { id: string }): Ask {
  const sectors = (a.sectors ?? []).map(asSector).filter((s): s is string => !!s);
  const types = (a.types ?? []).map(asType).filter((t) => t !== "Unknown");
  const states = (a.states ?? []).map((s) => String(s).trim().toUpperCase()).filter((s) => /^[A-Z]{2}$/.test(s));
  const regions = (a.regions ?? []).filter((r) => REGIONS.includes(r));
  const listPull = !sectors.length && !types.length && (regions.length > 0 || states.length > 0);
  return {
    id: String(a.id),
    client: (a.client || "").trim() || "Untitled ask",
    text: a.text || "",
    received: a.received,
    sectors,
    types,
    excludeTypes: a.excludeTypes ? a.excludeTypes.map(asType) : ["Service provider"],
    capitalOnly: a.capitalOnly ?? !listPull,
    minCheck: a.minCheck || null,
    maxCheck: a.maxCheck || null,
    regions,
    states,
    preferRegions: (a.preferRegions ?? []).filter((r) => REGIONS.includes(r)),
    size: Math.max(1, Math.min(1000, Math.round(a.size || 40))),
    view: a.view === "named" || a.view === "full" || a.view === "blind" ? a.view : listPull ? "full" : "blind",
    createdAt: a.createdAt,
  };
}

/** An entry of the synthetic sponsor_asks.json (the shape of Peter's three asks from the Nov 2 call). */
export interface SharedAsk {
  ask_id: string;
  requester: string;
  received?: string;
  ask?: string;
  target_sectors?: string[];
  target_types?: string[];
  target_regions?: string[];
  target_states?: string[];
  min_check?: number | null;
  list_size?: number;
  client_view?: ClientView;
}

export function fromSharedAsk(a: SharedAsk): Ask {
  const states = a.target_states ?? [];
  const sectors = a.target_sectors ?? [];
  const listPull = !sectors.length && ((a.target_regions ?? []).length > 0 || states.length > 0);
  const text = a.ask || "";
  return normalizeAsk({
    id: a.ask_id,
    client: a.requester,
    text,
    received: a.received,
    sectors,
    types: a.target_types ?? [],
    minCheck: a.min_check || null,
    regions: states.length ? [] : a.target_regions ?? [],
    states,
    size: a.list_size || 40,
    capitalOnly: !listPull,
    // a deal ask stays blind so nobody goes around Peter; a list he hands over shows names;
    // an event host needs contact details to send invitations
    view: a.client_view || (listPull ? "full" : /\blist\b/i.test(text) ? "named" : "blind"),
  });
}

const STATE_NAMES: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA", colorado: "CO", connecticut: "CT", delaware: "DE",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY",
  louisiana: "LA", maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO",
  montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM", "new york": "NY",
  "north carolina": "NC", "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI",
  "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT", virginia: "VA",
  washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY", "southern california": "CA", socal: "CA", "bay area": "CA",
};
const TYPE_WORDS: [RegExp, string][] = [
  [/multi[- ]?family office|\bmfo/i, "Multi-family office"],
  [/(?<!multi[- ])family office|\bsfo\b/i, "Family office"],
  [/\buhnw|ultra[- ]high/i, "UHNW individual"],
  [/\bhnw\b|high[- ]net|accredited/i, "HNW individual"],
  [/independent sponsor/i, "Independent sponsor"],
  [/\bpe\b|private equity|fund gp/i, "PE fund GP"],
  [/\blps?\b|allocator|endowment/i, "LP / fund allocator"],
];

const STATE_CODES = new Set(Object.values(STATE_NAMES));
/** Two-letter codes that are also everyday words. */
const AMBIGUOUS_CODES = new Set(["IN", "OR", "ME", "OK", "HI"]);
/** The people a sponsor asks for. A place tied to them ("family offices in the Mountain West", "Texas LPs") limits
 *  the list; a place anywhere else ("a Colorado carve-out", "our plant in Ohio") is where the deal is. */
const WHO = "investors?|family offices?|offices|lps?|folks|people|names|everyone|everybody|anyone|contacts|guests|attendees|check writers?|individuals|families|u?hnws?|independent sponsors|sponsors|groups|allocators|gps";
const WHO_PHRASE = `(?:${WHO})(?:\\s*(?:,|and|or)\\s+(?:[\\w-]+\\s+){0,2}(?:${WHO}))*`;
const PLACE_AFTER_WHO = new RegExp(`\\b${WHO_PHRASE}\\s+(?:(?:who are |that are )?(?:based|located|living)\\s+)?(?:in|from|across|around|near|throughout)\\s+(?:the\\s+)?([^.;!?]{2,90})`, "gi");
const CLAUSE_END = /\b(?:who|whom|with|that|writing|for|minimum|min|at least|checks?|send|please|looking|ideally|preferably|but|except)\b|\$|\d/i;
const PLACE_NAMES: [string, string | null, string | null][] = [
  ...Object.entries(STATE_NAMES).map(([name, ab]): [string, string | null, string | null] => [name, ab, null]),
  ...ASK_REGIONS.map((r): [string, string | null, string | null] => [r.toLowerCase(), null, r]),
];
const PLACE_BEFORE_WHO = PLACE_NAMES.map(([name, ab, region]) => [new RegExp(`\\b${name}(?:-based)?\\s+(?:${WHO})\\b`, "i"), ab, region] as const);

function placesIn(seg: string, states: Set<string>, regions: Set<string>) {
  const low = seg.toLowerCase();
  for (const [name, ab, region] of PLACE_NAMES) {
    if (!new RegExp(`\\b${name}\\b`).test(low)) continue;
    if (ab) states.add(ab);
    if (region) regions.add(region);
  }
  for (const m of seg.matchAll(/\b([A-Z]{2})\b/g)) if (STATE_CODES.has(m[1]) && !AMBIGUOUS_CODES.has(m[1])) states.add(m[1]);
}

/** A best guess at the filters from a pasted sponsor note, without Claude. Peter checks the form after. */
export function guessAskFromText(text: string): Partial<Ask> {
  const t = text || "";
  const sectors = categorize({ notes: t }).sectors;
  const types = TYPE_WORDS.filter(([r]) => r.test(t)).map(([, ty]) => ty);
  let minCheck: number | null = null;
  const chk = /(?:min(?:imum)?|at least|checks? (?:of|from)?|tickets? (?:of|from)?)\s*(?:check|ticket)?\s*(?:of|is)?\s*(\$?\s*\d[\d,.]*\s*(?:k|m|mm|million|thousand)?)/i.exec(t);
  if (chk) minCheck = parseMoneyRange(chk[1])[0];
  const states = new Set<string>();
  const regions = new Set<string>();
  for (const m of t.matchAll(PLACE_AFTER_WHO)) placesIn(m[1].split(CLAUSE_END)[0], states, regions);
  for (const [rx, ab, region] of PLACE_BEFORE_WHO) {
    if (!rx.test(t)) continue;
    if (ab) states.add(ab);
    if (region) regions.add(region);
  }
  const size = /\b(\d{1,3})\s*(?:people|names|contacts|investors|folks)\b/i.exec(t);
  return {
    sectors,
    types,
    minCheck,
    states: [...states],
    regions: states.size ? [] : [...regions],
    size: size ? +size[1] : undefined,
    view: /\binvit|\bevent|\bdinner/i.test(t) ? "full" : /\blist\b/i.test(t) ? "named" : undefined,
  };
}

/* ------------------------------------------------------------------ scoring */

function dealSectorMap(pastDeals?: PastDeal[]): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const d of pastDeals ?? []) {
    const raw = Array.isArray(d.sectors) ? d.sectors : String(d.sectors || "").split("|");
    const secs = raw.map((s) => asSector(s.trim())).filter((s): s is string => !!s);
    m.set(d.deal_id, secs);
    if (d.name) m.set(d.name, secs);
  }
  return m;
}

function overlaps(aLo: number | null, aHi: number | null, bLo: number | null, bHi: number | null) {
  return Math.max(aLo ?? 0, bLo ?? 0) <= Math.min(aHi ?? Infinity, bHi ?? Infinity);
}

const short = (s: string, n = 140) => (s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s);

/** Score one person against one ask. Returns null when the person is outside the ask's hard limits
 *  (geography, excluded types): those people are not "misses", they were never in scope. */
export function scorePerson(c: ListContact, ask: Ask, env: EngineEnv & { deals?: Map<string, string[]> } = {}): Scored | null {
  const type = asType(c.ty);
  if (ask.states.length ? !ask.states.includes((c.st || "").toUpperCase()) : ask.regions.length > 0 && !ask.regions.includes(c.rg || "")) return null;
  if (ask.excludeTypes.includes(type)) return null;

  const deals = env.deals ?? dealSectorMap(env.pastDeals);
  const today = env.today || todayIso();
  const key = keyOf(c);
  const own = env.overlay?.(key) ?? null;
  // ask sectors are all known names, so plain membership in the person's lists is enough
  const se = c.se ?? [];
  const extra = own?.sectors;
  const covers = (s: string) => se.includes(s) || (!!extra && extra.includes(s));
  const want = ask.sectors;
  const why: string[] = [];
  const clientWhy: string[] = [];
  const misses: string[] = [];
  const heldFor: string[] = [];
  const peterOnly: string[] = [];
  let score = 0;
  const signals: Scored["signals"] = { asked: [], askedWhen: "", invested: [], investedCount: 0, typeMatch: false, checkFit: false, close: false };

  if (ask.capitalOnly && !CAPITAL_TYPES.has(type)) misses.push(type === "Unknown" ? "Investor type unknown" : `Not a check writer (${type})`);

  const focus = want.filter(covers);
  if (want.length) {
    if (covers(want[0])) score += 40;
    else if (focus.length) score += 22;
    if (focus.length) why.push(`Focus: ${focus.join(", ")}`);
    else misses.push("No sector fit");
    const asked = askedSectors(c);
    const askedHit = want.filter((s) => asked.has(s));
    if (askedHit.length) {
      score += 12;
      const when = monthLabel(asked.get(askedHit[0]));
      signals.asked = askedHit;
      signals.askedWhen = when;
      why.push(`Asked Peter for ${askedHit.join(", ")} deals${when ? ` (${when})` : ""}`);
      clientWhy.push(`Has asked for ${askedHit.join(", ")} deals${when ? ` (${when})` : ""}`);
    }
  }
  const inv = investedSectors(c, deals);
  const invHit = want.filter((s) => inv.sectors.has(s));
  signals.invested = invHit;
  signals.investedCount = inv.count;
  if (invHit.length) {
    score += 15;
    why.push(`Invested alongside Peter in ${invHit.join(", ")}`);
    clientWhy.push(`Has co-invested with Peter in ${invHit.join(", ")}`);
  } else if (inv.count) {
    score += 6;
    why.push(`Invested in ${inv.count === 1 ? "one" : inv.count} of Peter's past deals`);
    clientWhy.push(`Has invested in ${inv.count === 1 ? "one" : inv.count} of Peter's past deals`);
  }

  if (ask.types.length) {
    if (ask.types.includes(type)) {
      score += 20;
      why.push(type);
      signals.typeMatch = true;
    } else if (CAPITAL_TYPES.has(type)) score += 6;
  } else if (CAPITAL_TYPES.has(type)) {
    score += 10;
    why.push(type);
  }

  const band = bandOf(c);
  if (ask.minCheck || ask.maxCheck) {
    const chk = statedCheck(c);
    if (!chk || !CAPITAL_TYPES.has(type)) score += 3;
    else if (overlaps(chk.min, chk.max, ask.minCheck, ask.maxCheck)) {
      score += 12;
      why.push(`Check size fits (${band})`);
      signals.checkFit = true;
    } else if (ask.minCheck && (chk.max ?? chk.min ?? 0) < ask.minCheck) misses.push(`Checks too small (${band})`);
    else misses.push(`Checks too large (${band})`);
  }

  if (c.rg && ask.preferRegions.includes(c.rg)) {
    score += 8;
    why.push(`Based in ${c.rg}`);
    clientWhy.push(`Based in ${c.rg}`);
  }

  const warmth = Math.max(1, Math.min(5, Math.round(c.rs || 1)));
  score += warmth * 4;
  if (warmth >= 4) {
    why.push("Close relationship with Peter");
    clientWhy.push("Close relationship with Peter");
    signals.close = true;
  }

  // private: life events and standing holds, Peter's own hold, sector holds, things they said no to
  heldFor.push(...autoHolds(c, today));
  for (const f of c.fl ?? []) {
    if (f.flag === "sold company" && (!f.date || (f.date < today && daysBetween(f.date, today) < 540))) {
      score += 8;
      peterOnly.push(`Fresh liquidity${f.date ? ` (${monthLabel(f.date)})` : ""}`);
    }
  }
  if (own?.hold) heldFor.push(own.note ? `Your hold: ${short(own.note, 80)}` : "Your hold");
  if (own?.noPitch) heldFor.push("Marked do not pitch");
  if (own?.note && !own.hold) peterOnly.push(`Your note: ${short(own.note)}`);
  if (c.ji) {
    const s = asSector(c.ji.sector);
    if (s && want.includes(s) && (!c.ji.date || daysBetween(c.ji.date, today) < 365)) heldFor.push(`Just invested in ${s}${c.ji.date ? ` (${monthLabel(c.ji.date)})` : ""}`);
  }
  for (const b of c.bu ?? []) if (want.includes(b)) heldFor.push(`Burned on a past ${b} deal`);
  const prefs = saidNoTo(c);
  if (prefs.length) {
    const askWords = `${ask.text} ${ask.client}`;
    for (const p of prefs) {
      const rule = NO_TO[p];
      if (rule && rule[0].test(askWords)) heldFor.push(rule[1]);
    }
  }

  return { c, key, score, why, clientWhy, misses, heldFor: heldFor.length > 1 ? [...new Set(heldFor)] : heldFor, peterOnly, band, focus, type, signals };
}

const byScore = (a: Scored, b: Scored) => b.score - a.score || (b.c.rs || 0) - (a.c.rs || 0) || a.c.n.localeCompare(b.c.n);

export function buildList(contacts: readonly ListContact[], ask: Ask, env: EngineEnv = {}): ListResult {
  const scoreEnv = { ...env, deals: dealSectorMap(env.pastDeals) };
  const scored: Scored[] = [];
  for (const c of contacts) {
    const s = scorePerson(c, ask, scoreEnv);
    if (s) scored.push(s);
  }
  scored.sort(byScore);
  const recommended: Scored[] = [];
  const held: Scored[] = [];
  const near: Scored[] = [];
  for (const s of scored) {
    if (!s.misses.length && s.heldFor.length) held.push(s);
    else if (!s.misses.length && !s.heldFor.length) {
      if (recommended.length < ask.size) recommended.push(s);
    } else if (s.misses.length === 1 && !s.heldFor.length && (s.c.rs || 0) >= 4 && near.length < 25) near.push(s);
  }
  return { ask, recommended, held, near, considered: scored.length };
}

/** Who goes in the sponsor file: recommended minus Peter's N's, plus anyone he said Y to elsewhere. */
export function keptPeople(list: ListResult, state: ReviewState | null, contacts: readonly ListContact[], env: EngineEnv = {}): Scored[] {
  const d = state?.decisions ?? {};
  const out: Scored[] = list.recommended.filter((s) => d[s.key] !== "N");
  const seen = new Set(out.map((s) => s.key));
  for (const s of [...list.held, ...list.near])
    if (d[s.key] === "Y" && !seen.has(s.key)) {
      out.push(s);
      seen.add(s.key);
    }
  if (state?.added?.length) {
    const want = new Set(state.added.filter((k) => !seen.has(k) && d[k] !== "N"));
    const byKey = new Map<string, ListContact>();
    for (const c of contacts) if (want.has(keyOf(c))) byKey.set(keyOf(c), c);
    const deals = dealSectorMap(env.pastDeals);
    for (const k of state.added) {
      const c = byKey.get(k);
      if (!c || seen.has(k)) continue;
      const s = scorePerson(c, { ...list.ask, states: [], regions: [], excludeTypes: [] }, { ...env, deals });
      // someone added by hand who carries a private hold still needs an explicit Y
      if (s && (!s.heldFor.length || d[k] === "Y")) {
        out.push(s);
        seen.add(k);
      }
    }
  }
  return out;
}

/* ------------------------------------------------------------------ what the sponsor gets */

/** Stable per-ask code, so a sponsor can ask "intro me to #K7F3Q" without ever seeing a name. */
export function blindRef(askId: string, key: string): string {
  const s = `${askId}:${key}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  // murmur3 finalizer, so every output bit depends on every input bit
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  const A = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
  let out = "";
  for (let i = 0, x = h >>> 0; i < 5; i++, x >>>= 5) out += A[x & 31];
  return "#" + out;
}

function seniority(title: string): string {
  const t = title.toLowerCase();
  if (/founder|principal|owner|partner|chair|president|\bceo\b|\bcio\b|patriarch|matriarch/.test(t)) return "Principal";
  if (/director|head|\bmd\b|\bvp\b|vice/.test(t)) return "Senior investment professional";
  return "Investor";
}

function profile(s: Scored): string {
  if (s.type === "UHNW individual" || s.type === "HNW individual") return s.type;
  if (s.type === "Unknown") return seniority(s.c.ti || "");
  return `${seniority(s.c.ti || "")}, ${s.type}`;
}

function phone(p?: string) {
  const d = (p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || "";
}

const regionText = (rg?: string) => (!rg ? "Not stated" : rg === "Other" ? "Other region" : rg);

export type ClientRow = Record<string, string | number>;

/** Rows for the sponsor file. Built only from safe fields: never notes, holds, Peter-only context or scores. */
export function clientRows(ask: Ask, kept: Scored[], view: ClientView): ClientRow[] {
  const used = new Set<string>();
  const refFor = (key: string) => {
    let ref = blindRef(ask.id, key);
    for (let n = 2; used.has(ref); n++) ref = blindRef(ask.id, `${key}#${n}`);
    used.add(ref);
    return ref;
  };
  return kept.map((s, i) => {
    const sectorText = (s.focus.length ? s.focus : (s.c.se ?? []).filter((x) => SECTOR_SET.has(x))).join(", ");
    const capital = CAPITAL_TYPES.has(s.type);
    const check = capital && s.band !== "Unknown" && s.band !== "Not an investor" ? s.band : "";
    const reasons = s.clientWhy.join("; ");
    if (view === "blind") {
      return {
        "#": i + 1,
        "Ref": refFor(s.key),
        "Profile": profile(s),
        "Region": regionText(s.c.rg),
        "Sector focus": sectorText || "Generalist",
        "Typical check": check || "On request",
        "Why relevant": reasons,
      };
    }
    const row: ClientRow = {
      "#": i + 1,
      "Name": s.c.n,
      "Firm": s.c.co || "",
      "Title": s.c.ti || "",
      "Region": s.c.rg && s.c.rg !== "Other" ? s.c.rg : "",
      "Investor type": s.type === "Unknown" ? "" : s.type,
      "Sector focus": sectorText,
      "Typical check": check,
      "Why relevant": reasons,
    };
    if (view === "full") Object.assign(row, { "Email": s.c.em || "", "Phone": phone(s.c.ph), "City": s.c.ci || "", "State": s.c.st || "" });
    return row;
  });
}

/** Peter's own working copy: everyone considered, with every private reason. Never for a sponsor. */
export function reviewRows(list: ListResult, state: ReviewState | null): ClientRow[] {
  const d = state?.decisions ?? {};
  const row = (s: Scored, section: string, rank: number | ""): ClientRow => ({
    "Section": section,
    "Rank": rank,
    "Keep (Y/N)": d[s.key] || (section === "Recommended" ? "Y" : ""),
    "Score": s.score,
    "Name": s.c.n,
    "Firm": s.c.co || "",
    "Title": s.c.ti || "",
    "Type": s.type,
    "Region": s.c.rg || "",
    "State": s.c.st || "",
    "Sectors": (s.c.se ?? []).join(", "),
    "Check": s.band,
    "Warmth": s.c.rs || "",
    "Why": s.why.join("; "),
    "Misses": s.misses.join("; "),
    "Held back for (private)": s.heldFor.join("; "),
    "Only you see (private)": s.peterOnly.join("; "),
    "Email": s.c.em || "",
    "Phone": phone(s.c.ph),
  });
  return [
    ...list.recommended.map((s, i) => row(s, "Recommended", i + 1)),
    ...list.held.map((s) => row(s, "Held back", "")),
    ...list.near.map((s) => row(s, "Close miss", "")),
  ];
}

/** Rows as a header plus value rows, for a spreadsheet writer. */
export function toTable(rows: ClientRow[], columns?: string[]): (string | number)[][] {
  const cols = columns ?? (rows.length ? Object.keys(rows[0]) : []);
  return [cols, ...rows.map((r) => cols.map((c) => r[c] ?? ""))];
}

/** Excel-friendly CSV (UTF-8 BOM, CRLF). Cells that would run as formulas are neutralized. */
export function toCsv(rows: ClientRow[], columns?: string[]): string {
  const cell = (v: unknown) => {
    let s = v == null ? "" : String(v);
    if (/^[=+\-@\t\r]/.test(s) && !/^[+-]?[\d(][\d\s().-]*$/.test(s)) s = "'" + s;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return "﻿" + toTable(rows, columns).map((r) => r.map(cell).join(",")).join("\r\n") + "\r\n";
}

export function fileStem(ask: Ask, today = todayIso()): string {
  const who = ask.client.replace(/\(.*?\)/g, "").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || ask.id;
  return `${who}-${today}`;
}

/* ------------------------------------------------------------------ network snapshot (no names) */

export interface SnapshotRow {
  sector: string;
  people: number;
  close: number;
  big: number;
  byType: Record<string, number>;
}
export interface Snapshot {
  people: number;
  checkWriters: number;
  onHold: number;
  rows: SnapshotRow[];
  byType: Record<string, number>;
  byRegion: Record<string, number>;
  byBand: Record<string, number>;
}

/** Whether a person is on any private hold today (life event, standing hold, or Peter's own). */
export function onHold(c: ListContact, env: EngineEnv = {}): boolean {
  const own = env.overlay?.(keyOf(c));
  return !!own?.hold || !!own?.noPitch || autoHolds(c, env.today || todayIso()).length > 0;
}

/** What Peter can quote a sponsor: check writers with no private hold, by sector and type. No names. */
export function snapshot(contacts: readonly ListContact[], env: EngineEnv = {}): Snapshot {
  const rows = new Map<string, SnapshotRow>();
  const byType: Record<string, number> = {};
  const byRegion: Record<string, number> = {};
  const byBand: Record<string, number> = {};
  let checkWriters = 0;
  let held = 0;
  for (const c of contacts) {
    const type = asType(c.ty);
    if (onHold(c, env)) {
      held++;
      continue;
    }
    if (!CAPITAL_TYPES.has(type)) continue;
    checkWriters++;
    byType[type] = (byType[type] || 0) + 1;
    if (c.rg) byRegion[c.rg] = (byRegion[c.rg] || 0) + 1;
    const band = bandOf(c);
    byBand[band] = (byBand[band] || 0) + 1;
    const own = env.overlay?.(keyOf(c))?.sectors ?? [];
    for (const s of new Set([...(c.se ?? []), ...own])) {
      if (!SECTOR_SET.has(s)) continue;
      const r = rows.get(s) ?? { sector: s, people: 0, close: 0, big: 0, byType: {} };
      r.people++;
      if ((c.rs || 0) >= 4) r.close++;
      if (band === "$2M-$10M" || band === "$10M+") r.big++;
      r.byType[type] = (r.byType[type] || 0) + 1;
      rows.set(s, r);
    }
  }
  return { people: contacts.length, checkWriters, onHold: held, rows: [...rows.values()].sort((a, b) => b.people - a.people), byType, byRegion, byBand };
}

export function snapshotRows(s: Snapshot): ClientRow[] {
  const types = [...CAPITAL_TYPES];
  return s.rows.map((r) => {
    const row: ClientRow = { "Sector": r.sector, "Check writers": r.people, "Close relationships": r.close, "$2M+ checks": r.big };
    for (const t of types) row[t] = r.byType[t] || 0;
    return row;
  });
}

/* ------------------------------------------------------------------ search (manual adds, MCP) */

export function searchPeople(contacts: readonly ListContact[], q: string, limit = 8): ListContact[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const hits: [number, ListContact][] = [];
  for (const c of contacts) {
    const hay = `${c.n} ${c.co || ""} ${c.lco || ""} ${c.ti || ""} ${c.ci || ""} ${c.em || ""}`.toLowerCase();
    if (!words.every((w) => hay.includes(w))) continue;
    const name = c.n.toLowerCase();
    hits.push([(name.startsWith(words[0]) ? 2 : 0) + (name.includes(words.join(" ")) ? 1 : 0) + (c.rs || 0) / 10, c]);
  }
  return hits.sort((a, b) => b[0] - a[0]).slice(0, limit).map((h) => h[1]);
}
